import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
    Eye,
    EyeOff,
    Shield,
    CheckCircle,
    AlertCircle,
    Loader2,
    ChevronDown,
    X,
    Info,
    Search,
    AlertTriangle,
    Trash2,
    FileText,
    Calculator,
    Lock,
    ExternalLink,
    RefreshCw,
    Link2Off,
} from 'lucide-react';
import Layout from './Layout';
import { getXeroConfigApi, saveXeroConfigApi, deleteXeroConfigApi, getXeroReferenceDataApi } from '../services/xeroApi';
import { listAccountingIntegrationsApi, toggleAccountingIntegrationApi } from '../services/accountingIntegrationApi';
import {
    getMyobAuthorizeUrlApi,
    getMyobConnectionApi,
    getMyobReferenceDataApi,
    saveMyobDefaultsApi,
    disconnectMyobApi,
} from '../services/myobApi';
import {
    getQuickBooksAuthorizeUrlApi,
    getQuickBooksConnectionApi,
    getQuickBooksReferenceDataApi,
    saveQuickBooksDefaultsApi,
    disconnectQuickBooksApi,
} from '../services/quickbooksApi';

// ============================================
// Accounting Software Tabs
// ============================================

const ACCOUNTING_TABS = [
    { id: 'xero',       softwareKey: 'XERO',       label: 'Xero' },
    { id: 'myob',       softwareKey: 'MYOB',       label: 'MYOB' },
    { id: 'quickbooks', softwareKey: 'QUICKBOOKS', label: 'QuickBooks' },
];

// ============================================
// Xero API Scopes Reference
// ============================================

const XERO_API_SCOPES = [
    { value: 'accounting.transactions', label: 'Accounting Transactions', category: 'Accounting', description: 'Full access to transactions' },
    { value: 'accounting.transactions.read', label: 'Accounting Transactions (Read)', category: 'Accounting', description: 'Read-only access to transactions' },
    { value: 'accounting.reports.read', label: 'Accounting Reports (Read)', category: 'Accounting', description: 'Read access to reports' },
    { value: 'accounting.reports.tenninetynine.read', label: 'Accounting Reports 1099 (Read)', category: 'Accounting', description: 'Read access to 1099 reports' },
    { value: 'accounting.budgets.read', label: 'Accounting Budgets (Read)', category: 'Accounting', description: 'Read access to budgets' },
    { value: 'accounting.journals.read', label: 'Accounting Journals (Read)', category: 'Accounting', description: 'Read access to journals' },
    { value: 'accounting.settings', label: 'Accounting Settings', category: 'Accounting', description: 'Full access to settings' },
    { value: 'accounting.settings.read', label: 'Accounting Settings (Read)', category: 'Accounting', description: 'Read-only access to settings' },
    { value: 'accounting.contacts', label: 'Accounting Contacts', category: 'Accounting', description: 'Full access to contacts' },
    { value: 'accounting.contacts.read', label: 'Accounting Contacts (Read)', category: 'Accounting', description: 'Read-only access to contacts' },
    { value: 'accounting.attachments', label: 'Accounting Attachments', category: 'Accounting', description: 'Full access to attachments' },
    { value: 'accounting.attachments.read', label: 'Accounting Attachments (Read)', category: 'Accounting', description: 'Read-only access to attachments' },
    { value: 'assets', label: 'Assets', category: 'Assets', description: 'Full access to fixed assets' },
    { value: 'assets.read', label: 'Assets (Read)', category: 'Assets', description: 'Read-only access to fixed assets' },
    { value: 'files', label: 'Files', category: 'Files', description: 'Full access to files' },
    { value: 'files.read', label: 'Files (Read)', category: 'Files', description: 'Read-only access to files' },
    { value: 'payroll.employees', label: 'Payroll Employees', category: 'Payroll', description: 'Full access to employee data' },
    { value: 'payroll.employees.read', label: 'Payroll Employees (Read)', category: 'Payroll', description: 'Read-only access to employee data' },
    { value: 'payroll.payruns', label: 'Payroll Pay Runs', category: 'Payroll', description: 'Full access to pay runs' },
    { value: 'payroll.payruns.read', label: 'Payroll Pay Runs (Read)', category: 'Payroll', description: 'Read-only access to pay runs' },
    { value: 'payroll.payslip', label: 'Payroll Payslips', category: 'Payroll', description: 'Full access to payslips' },
    { value: 'payroll.payslip.read', label: 'Payroll Payslips (Read)', category: 'Payroll', description: 'Read-only access to payslips' },
    { value: 'payroll.settings', label: 'Payroll Settings', category: 'Payroll', description: 'Full access to payroll settings' },
    { value: 'payroll.settings.read', label: 'Payroll Settings (Read)', category: 'Payroll', description: 'Read-only access to payroll settings' },
    { value: 'payroll.timesheets', label: 'Payroll Timesheets', category: 'Payroll', description: 'Full access to timesheets' },
    { value: 'payroll.timesheets.read', label: 'Payroll Timesheets (Read)', category: 'Payroll', description: 'Read-only access to timesheets' },
    { value: 'projects', label: 'Projects', category: 'Projects', description: 'Full access to projects' },
    { value: 'projects.read', label: 'Projects (Read)', category: 'Projects', description: 'Read-only access to projects' },
];

const SCOPE_CATEGORIES = ['Accounting', 'Assets', 'Files', 'Payroll', 'Projects'];

// ============================================
// Helper: find which integration (if any) is active
// ============================================

const getActiveIntegration = (integrations) =>
    integrations.find(i => i.enabled) || null;

// ============================================
// Scope Selector Modal
// ============================================

const ScopeSelectorModal = ({ isOpen, onClose, selectedScopes, onScopesChange }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCategories, setExpandedCategories] = useState(new Set(SCOPE_CATEGORIES));
    const [localSelectedScopes, setLocalSelectedScopes] = useState(selectedScopes);

    useEffect(() => {
        setLocalSelectedScopes(selectedScopes);
    }, [selectedScopes, isOpen]);

    if (!isOpen) return null;

    const toggleScope = (scopeValue) => {
        setLocalSelectedScopes(prev =>
            prev.includes(scopeValue)
                ? prev.filter(s => s !== scopeValue)
                : [...prev, scopeValue]
        );
    };

    const toggleCategory = (category) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(category)) next.delete(category);
            else next.add(category);
            return next;
        });
    };

    const selectAllInCategory = (category) => {
        const categoryScopes = XERO_API_SCOPES.filter(s => s.category === category).map(s => s.value);
        const allSelected = categoryScopes.every(s => localSelectedScopes.includes(s));
        if (allSelected) {
            setLocalSelectedScopes(prev => prev.filter(s => !categoryScopes.includes(s)));
        } else {
            setLocalSelectedScopes(prev => [...new Set([...prev, ...categoryScopes])]);
        }
    };

    const filteredScopes = XERO_API_SCOPES.filter(scope =>
        scope.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        scope.value.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const groupedScopes = SCOPE_CATEGORIES.reduce((acc, category) => {
        acc[category] = filteredScopes.filter(s => s.category === category);
        return acc;
    }, {});

    const handleApply = () => {
        onScopesChange(localSelectedScopes);
        onClose();
    };

    const modalContent = (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-emerald-50/50">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900">Select API Scopes</h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {localSelectedScopes.length} of {XERO_API_SCOPES.length} scopes selected
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Search and bulk actions */}
                <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search scopes..."
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                            autoFocus
                        />
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setLocalSelectedScopes(XERO_API_SCOPES.map(s => s.value))}
                            className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-lg hover:bg-emerald-200 transition-colors"
                        >
                            Select All
                        </button>
                        <button
                            type="button"
                            onClick={() => setLocalSelectedScopes([])}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            Clear All
                        </button>
                    </div>
                </div>

                {/* Scope List */}
                <div className="flex-1 overflow-y-auto">
                    {SCOPE_CATEGORIES.map(category => {
                        const categoryScopes = groupedScopes[category];
                        if (!categoryScopes || categoryScopes.length === 0) return null;

                        const isExpanded = expandedCategories.has(category);
                        const allSelected = categoryScopes.every(s => localSelectedScopes.includes(s.value));

                        return (
                            <div key={category} className="border-b border-gray-100 last:border-b-0">
                                <div
                                    className="flex items-center justify-between px-5 py-3.5 bg-gray-50/80 hover:bg-gray-100/80 cursor-pointer sticky top-0 z-10"
                                    onClick={() => toggleCategory(category)}
                                >
                                    <div className="flex items-center">
                                        <ChevronDown
                                            size={18}
                                            className={`text-gray-400 mr-2 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
                                        />
                                        <span className="font-semibold text-gray-900">{category}</span>
                                        <span className="ml-2 px-2 py-0.5 text-xs font-medium text-gray-500 bg-gray-200 rounded-full">
                                            {categoryScopes.filter(s => localSelectedScopes.includes(s.value)).length}/{categoryScopes.length}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); selectAllInCategory(category); }}
                                        className={`text-xs font-medium px-3 py-1 rounded-lg transition-colors ${
                                            allSelected
                                                ? 'text-red-600 bg-red-50 hover:bg-red-100'
                                                : 'text-emerald-700 bg-emerald-100 hover:bg-emerald-200'
                                        }`}
                                    >
                                        {allSelected ? 'Deselect all' : 'Select all'}
                                    </button>
                                </div>

                                {isExpanded && (
                                    <div className="py-1">
                                        {categoryScopes.map(scope => (
                                            <label
                                                key={scope.value}
                                                className={`flex items-start px-5 py-3 cursor-pointer transition-colors ${
                                                    localSelectedScopes.includes(scope.value)
                                                        ? 'bg-emerald-50/50'
                                                        : 'hover:bg-gray-50'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={localSelectedScopes.includes(scope.value)}
                                                    onChange={() => toggleScope(scope.value)}
                                                    className="mt-0.5 h-4 w-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 focus:ring-offset-0"
                                                />
                                                <div className="ml-3 flex-1">
                                                    <span className={`block text-sm font-medium ${
                                                        localSelectedScopes.includes(scope.value) ? 'text-emerald-700' : 'text-gray-900'
                                                    }`}>
                                                        {scope.label}
                                                    </span>
                                                    <span className="block text-xs text-gray-500 mt-0.5 font-mono">
                                                        {scope.value}
                                                    </span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50/50">
                    <p className="text-sm text-gray-600">
                        <span className="font-semibold text-emerald-700">{localSelectedScopes.length}</span> scopes selected
                    </p>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleApply}
                            className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-colors"
                        >
                            Apply Selection
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modalContent, document.body);
};

// ============================================
// Delete Confirmation Modal
// ============================================

const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, deleting }) => {
    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                <div className="flex items-start gap-4 mb-6">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Delete Xero Configuration</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            This will permanently remove your Xero credentials and API scope settings. Any active integrations will stop working immediately.
                        </p>
                    </div>
                </div>
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={deleting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={deleting}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                        {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        {deleting ? 'Deleting...' : 'Delete Configuration'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// ============================================
// Integration Toggle
// ============================================

const IntegrationToggle = ({ label, enabled, toggling, onToggle, disabled, disabledReason }) => (
    <div className={`flex items-center justify-between p-4 bg-white border rounded-xl ${
        disabled && !enabled ? 'border-gray-100 bg-gray-50/50' : 'border-gray-200'
    }`}>
        <div className="flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${
                enabled ? 'bg-emerald-500' : disabled ? 'bg-gray-200' : 'bg-gray-300'
            }`} />
            <div>
                <p className={`text-sm font-medium ${disabled && !enabled ? 'text-gray-400' : 'text-gray-900'}`}>
                    Enable {label} Integration
                </p>
                <p className="text-xs text-gray-500">
                    {disabled && !enabled
                        ? disabledReason
                        : enabled
                            ? `${label} integration is active for this organisation`
                            : `Toggle on to activate ${label} integration`}
                </p>
            </div>
        </div>
        <button
            type="button"
            onClick={onToggle}
            disabled={toggling || (disabled && !enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed ${
                enabled ? 'bg-emerald-600' : 'bg-gray-200'
            }`}
        >
            {toggling ? (
                <Loader2 size={14} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin text-white" />
            ) : (
                <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                        enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
            )}
        </button>
    </div>
);

// ============================================
// Consent Integration Tab (MYOB, QuickBooks)
//
// Shaped nothing like the Xero tab, because their OAuth is nothing like Xero's.
// There is no credentials form: the owner clicks Connect, consents on the
// provider's own site, and comes back. What this screen manages afterwards is
// the consent (live, lapsed, gone) and the posting decisions the provider forces
// on us — which account, item or tax code to use, chosen from the workshop's own
// file, because every one of those is an ID that means nothing anywhere else.
//
// One component serves both providers. They differ only in wording, in which
// dropdowns they need, and in the shape of the save payload — all of which is
// data, passed in via the `fields` and copy props below. A second copy of this
// file would be four hundred lines that drift apart.
// ============================================

const ConsentIntegrationTab = ({
    name,
    softwareKey,
    queryKey,          // the ?<queryKey>=connected parameter the callback returns with
    api,               // { authorizeUrl, connection, referenceData, saveDefaults, disconnect }
    fields,            // dropdown definitions — see MYOB_TAB / QUICKBOOKS_TAB below
    optionId,          // how to read an option's id (providers disagree: uid vs id)
    describeCompany,   // connection -> the file/company name to show
    copy,              // provider-specific prose
    integrations,
    togglingKey,
    onToggle,
}) => {
    const integration = integrations.find(i => i.software === softwareKey);
    const enabled = integration?.enabled || false;

    const activeOther = getActiveIntegration(integrations);
    const disabledByOther = activeOther && activeOther.software !== softwareKey;
    const activeLabel = disabledByOther
        ? ACCOUNTING_TABS.find(t => t.softwareKey === activeOther.software)?.label || activeOther.software
        : null;

    const [loading, setLoading] = useState(true);
    const [connection, setConnection] = useState(null);
    const [connecting, setConnecting] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);

    const [referenceData, setReferenceData] = useState(null);
    const [referenceLoading, setReferenceLoading] = useState(false);

    const [selection, setSelection] = useState({});
    const [saving, setSaving] = useState(false);

    const [notice, setNotice] = useState(null);   // { tone: 'success' | 'warn' | 'error', text }

    /*
     * The return leg from the provider.
     *
     * The consent flow leaves the app entirely, so the outcome arrives as a query parameter
     * on a fresh page load rather than as a promise resolving. Read it once, then strip it
     * from the URL — leaving ?x=connected behind means a refresh re-announces a connection
     * that happened minutes ago.
     */
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const outcome = params.get(queryKey);
        if (!outcome) return;

        const reason = params.get('reason');
        if (outcome === 'connected') {
            setNotice({ tone: 'success', text: `${name} connected.` });
        } else if (outcome === 'declined') {
            setNotice({ tone: 'warn', text: reason || `${name} access was not granted.` });
        } else {
            setNotice({ tone: 'error', text: reason || `The ${name} connection could not be completed.` });
        }

        window.history.replaceState({}, '', window.location.pathname);
    }, [queryKey, name]);

    useEffect(() => {
        let cancelled = false;
        api.connection()
            .then(data => { if (!cancelled) setConnection(data); })
            .catch(err => {
                if (cancelled) return;
                setNotice({
                    tone: 'error',
                    text: err.response?.data?.message || `Could not load the ${name} connection.`,
                });
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    /*
     * Reference data is only meaningful once there is a live connection pointing at a real
     * file. Asking earlier just produces an error the user can do nothing with.
     */
    const connectedToFile = connection?.connected
        && !connection.needsReconnect
        && Boolean(describeCompany(connection));

    useEffect(() => {
        if (!connectedToFile) {
            setReferenceData(null);
            return;
        }

        let cancelled = false;
        setReferenceLoading(true);

        api.referenceData()
            .then(data => {
                if (cancelled) return;
                setReferenceData(data);
                // Open showing what is already saved, rather than empty dropdowns.
                setSelection(Object.fromEntries(
                    fields.map(f => [f.key, connection[f.connectionKey] || ''])));
            })
            .catch(err => {
                if (cancelled) return;
                setReferenceData({
                    loaded: false,
                    message: err.response?.data?.message || `Could not reach ${name}.`,
                });
            })
            .finally(() => { if (!cancelled) setReferenceLoading(false); });

        return () => { cancelled = true; };
    }, [connectedToFile]);

    // ── Actions ──────────────────────────────────────────────────────

    const handleConnect = async () => {
        setConnecting(true);
        setNotice(null);
        try {
            const result = await api.authorizeUrl();
            if (!result.available || !result.authorizeUrl) {
                setNotice({ tone: 'warn', text: result.message || `${name} is not available yet.` });
                setConnecting(false);
                return;
            }
            // A full navigation, not a popup: the consent screen is a sign-in flow, and popups
            // get blocked, swallowed by password managers, or lost on mobile.
            window.location.assign(result.authorizeUrl);
        } catch (err) {
            setNotice({
                tone: 'error',
                text: err.response?.data?.message || `Could not start the ${name} connection.`,
            });
            setConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        setDisconnecting(true);
        try {
            setConnection(await api.disconnect());
            setReferenceData(null);
            setSelection({});
            setNotice({ tone: 'success', text: `${name} disconnected.` });
            setIsDisconnectModalOpen(false);
        } catch (err) {
            setNotice({
                tone: 'error',
                text: err.response?.data?.message || `Could not disconnect ${name}.`,
            });
            setIsDisconnectModalOpen(false);
        } finally {
            setDisconnecting(false);
        }
    };

    const handleSaveDefaults = async () => {
        // Every field must resolve to a real option. A stale selection — an account deleted in
        // the provider since this page loaded — would otherwise be saved as an ID that fails
        // at reconcile time.
        const chosen = fields.map(f => ({
            field: f,
            option: (referenceData?.[f.optionsKey] || [])
                .find(o => optionId(o) === selection[f.key]),
        }));

        if (chosen.some(c => !c.option)) {
            setNotice({ tone: 'warn', text: copy.chooseAll });
            return;
        }

        setSaving(true);
        setNotice(null);
        try {
            // Names travel with the IDs so the connection can be described later without
            // calling the provider again on every page load.
            const payload = Object.assign({}, ...chosen.map(c => c.field.payload(c.option)));
            setConnection(await api.saveDefaults(payload));
            setNotice({ tone: 'success', text: `${name} posting settings saved.` });
        } catch (err) {
            setNotice({
                tone: 'error',
                text: err.response?.data?.message
                    || `Could not save the ${name} posting settings.`,
            });
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return 'N/A';
        return new Date(value).toLocaleDateString('en-AU', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    };

    // ── Loading ──────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[40vh]">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-500 text-sm">Loading {name} connection...</p>
                </div>
            </div>
        );
    }

    const connected = connection?.connected;
    const available = connection?.available !== false;
    const needsReconnect = connection?.needsReconnect;
    const readyToPush = connection?.readyToPush;

    /*
     * Why the enable toggle can be blocked.
     *
     * Enabling while unconnected, or connected but with nowhere to post, produces an
     * integration that shows a Reconcile button it cannot honour — the failure would surface
     * at reconcile time, on an invoice, in front of a customer. Cheaper to refuse here. The
     * server enforces the same rule via enablementBlocker(); this is the affordance.
     */
    const toggleBlockedReason = disabledByOther
        ? `Disable ${activeLabel} first to enable ${name}`
        : !available
            ? `${name} is not available on this environment yet`
            : !connected
                ? `Connect ${name} first`
                : needsReconnect
                    ? `Reconnect ${name} first`
                    : !readyToPush
                        ? copy.notReady
                        : null;

    // No max-width or horizontal padding here: the page shell already sets both.
    return (
        <div className="py-8">
            <div className="mb-6">
                <IntegrationToggle
                    label={name}
                    enabled={enabled}
                    toggling={togglingKey === softwareKey}
                    onToggle={() => onToggle(softwareKey, !enabled)}
                    disabled={Boolean(toggleBlockedReason)}
                    disabledReason={toggleBlockedReason}
                />
            </div>

            {/* Sandbox is worth shouting about: it looks identical to a real connection but
                writes to a throwaway company. */}
            {available && connection?.sandbox && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                        <span className="font-medium">Sandbox mode.</span>{' '}
                        This environment is pointed at {name}'s test companies. Nothing written
                        here reaches real books.
                    </p>
                </div>
            )}

            {notice && (
                <div className={`mb-6 p-4 rounded-xl flex items-start gap-3 border ${
                    notice.tone === 'success' ? 'bg-emerald-50 border-emerald-200'
                        : notice.tone === 'warn' ? 'bg-amber-50 border-amber-200'
                            : 'bg-red-50 border-red-200'
                }`}>
                    {notice.tone === 'success'
                        ? <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        : <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            notice.tone === 'warn' ? 'text-amber-600' : 'text-red-600'}`} />}
                    <p className={`text-sm font-medium ${
                        notice.tone === 'success' ? 'text-emerald-800'
                            : notice.tone === 'warn' ? 'text-amber-800' : 'text-red-800'
                    }`}>{notice.text}</p>
                </div>
            )}

            {/* ── Not available on this environment ───────────────────── */}
            {!available && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                        <Calculator className="w-8 h-8 text-gray-400" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{name} Integration</h2>
                    <p className="text-sm text-gray-500 max-w-md mx-auto">
                        {name} is not available on this environment yet.
                    </p>
                </div>
            )}

            {/* ── Connect ─────────────────────────────────────────────── */}
            {available && !connected && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
                    <div className="flex items-start gap-4 mb-6">
                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                            <Calculator className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 mb-1">Connect {name}</h2>
                            <p className="text-sm text-gray-500 max-w-xl">{copy.connectBlurb}</p>
                        </div>
                    </div>

                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-800">{copy.consentNote}</p>
                    </div>

                    <button
                        type="button"
                        onClick={handleConnect}
                        disabled={connecting}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    >
                        {connecting
                            ? <Loader2 size={16} className="animate-spin" />
                            : <ExternalLink size={16} />}
                        Connect to {name}
                    </button>
                </div>
            )}

            {/* ── Connected ───────────────────────────────────────────── */}
            {available && connected && (
                <div className="space-y-6">
                    <div className={`rounded-2xl border p-5 ${
                        needsReconnect ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'
                    }`}>
                        <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                needsReconnect ? 'bg-amber-100' : 'bg-emerald-100'
                            }`}>
                                {needsReconnect
                                    ? <AlertTriangle className="w-5 h-5 text-amber-600" />
                                    : <CheckCircle className="w-5 h-5 text-emerald-600" />}
                            </div>
                            <div className="flex-1">
                                <h3 className={`font-semibold mb-1 ${
                                    needsReconnect ? 'text-amber-900' : 'text-emerald-900'
                                }`}>
                                    {needsReconnect ? `${name} needs reconnecting` : `${name} connected`}
                                </h3>
                                <p className={`text-sm mb-3 ${
                                    needsReconnect ? 'text-amber-800' : 'text-emerald-800'
                                }`}>
                                    {needsReconnect
                                        ? (connection.lastError || `The ${name} connection has expired.`)
                                        : `Reconciled invoices will be recorded in this ${name} file.`}
                                </p>
                                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs ${
                                    needsReconnect ? 'text-amber-700' : 'text-emerald-700'
                                }`}>
                                    <div>
                                        <span className="font-medium">{copy.companyLabel}:</span>{' '}
                                        {describeCompany(connection) || 'Not resolved'}
                                    </div>
                                    <div>
                                        <span className="font-medium">Connected:</span>{' '}
                                        {formatDate(connection.connectedAt)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 mt-4">
                            <button
                                type="button"
                                onClick={handleConnect}
                                disabled={connecting}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                {connecting
                                    ? <Loader2 size={14} className="animate-spin" />
                                    : <RefreshCw size={14} />}
                                Reconnect
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsDisconnectModalOpen(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-xl hover:bg-red-50 transition-colors"
                            >
                                <Link2Off size={14} />
                                Disconnect
                            </button>
                        </div>
                    </div>

                    {/* ── Posting settings ────────────────────────────── */}
                    {!needsReconnect && (
                        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
                            <div className="flex items-start gap-3 mb-5">
                                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900">{copy.postingTitle}</h3>
                                    <p className="text-sm text-gray-500">{copy.postingBlurb}</p>
                                </div>
                            </div>

                            {referenceLoading && (
                                <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
                                    <Loader2 size={16} className="animate-spin" />
                                    Loading options from {name}...
                                </div>
                            )}

                            {!referenceLoading && referenceData && !referenceData.loaded && (
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-amber-800">
                                        {referenceData.message || `Could not load your ${name} settings.`}
                                    </p>
                                </div>
                            )}

                            {!referenceLoading && referenceData?.loaded && (
                                <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {fields.map(field => {
                                            const options = referenceData[field.optionsKey] || [];
                                            return (
                                                <div key={field.key}>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                        {field.label}
                                                    </label>
                                                    <select
                                                        value={selection[field.key] || ''}
                                                        onChange={(e) => setSelection(prev => ({
                                                            ...prev, [field.key]: e.target.value,
                                                        }))}
                                                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                                    >
                                                        <option value="">Select…</option>
                                                        {options.map(o => (
                                                            <option key={optionId(o)} value={optionId(o)}>
                                                                {field.format(o)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {field.hint && (
                                                        <p className="mt-1.5 text-xs text-gray-500">{field.hint}</p>
                                                    )}
                                                    {options.length === 0 && (
                                                        <p className="mt-1.5 text-xs text-amber-700">
                                                            {field.emptyHint}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="flex items-center justify-end mt-5">
                                        <button
                                            type="button"
                                            onClick={handleSaveDefaults}
                                            disabled={saving}
                                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                        >
                                            {saving && <Loader2 size={16} className="animate-spin" />}
                                            Save posting settings
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Disconnect confirmation. Explicit about what it does and does not do — people
                assume disconnecting here also revokes access at the provider's end, which is
                true for some providers and not others. */}
            {isDisconnectModalOpen && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-base font-semibold text-gray-900">Disconnect {name}?</h4>
                            <button onClick={() => setIsDisconnectModalOpen(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                                <X size={18} />
                            </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                            Wadakaroo will stop sending reconciled invoices to {name}, and your
                            posting choices are forgotten.
                        </p>
                        <p className="text-sm text-gray-500 mb-5">
                            Invoices already sent stay there. {copy.revokeNote}
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setIsDisconnectModalOpen(false)}
                                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDisconnect}
                                disabled={disconnecting}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                                {disconnecting && <Loader2 size={14} className="animate-spin" />}
                                Disconnect
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

// ── MYOB ────────────────────────────────────────────────────────────
//
// MYOB posts invoice lines to an ACCOUNT directly, and needs a separate bank
// account for the payment: two different questions that both look like "which
// account", hence the deliberately distinct labels.

const MYOB_TAB = {
    name: 'MYOB',
    softwareKey: 'MYOB',
    queryKey: 'myob',
    api: {
        authorizeUrl: getMyobAuthorizeUrlApi,
        connection: getMyobConnectionApi,
        referenceData: getMyobReferenceDataApi,
        saveDefaults: saveMyobDefaultsApi,
        disconnect: disconnectMyobApi,
    },
    optionId: (o) => o.uid,
    describeCompany: (c) => c.businessName || c.businessId,
    fields: [
        {
            key: 'incomeAccountUid',
            connectionKey: 'defaultIncomeAccountUid',
            optionsKey: 'incomeAccounts',
            label: 'Income account',
            format: (a) => (a.displayId ? `${a.displayId} — ${a.name}` : a.name),
            emptyHint: 'No income accounts found in this MYOB file.',
            payload: (a) => ({
                incomeAccountUid: a.uid,
                incomeAccountName: a.displayId ? `${a.displayId} ${a.name}` : a.name,
            }),
        },
        {
            key: 'taxCodeUid',
            connectionKey: 'defaultTaxCodeUid',
            optionsKey: 'taxCodes',
            label: 'Tax code',
            format: (t) => `${t.code}${t.description ? ` — ${t.description}` : ''}`,
            emptyHint: 'No tax codes found in this MYOB file.',
            payload: (t) => ({ taxCodeUid: t.uid, taxCode: t.code }),
        },
        {
            key: 'depositAccountUid',
            connectionKey: 'defaultDepositAccountUid',
            optionsKey: 'depositAccounts',
            label: 'Deposit payments into',
            hint: 'Where the money lands — not the same as the income account above.',
            format: (a) => (a.displayId ? `${a.displayId} — ${a.name}` : a.name),
            emptyHint: 'No bank accounts found in this MYOB file.',
            payload: (a) => ({
                depositAccountUid: a.uid,
                depositAccountName: a.displayId ? `${a.displayId} ${a.name}` : a.name,
            }),
        },
    ],
    copy: {
        companyLabel: 'Business',
        connectBlurb: "You'll be taken to MYOB to sign in and choose which business file "
            + 'Wadakaroo may use. Nothing is sent to MYOB until you reconcile a paid invoice.',
        consentNote: 'MYOB asks you to approve access again every so often. If the connection '
            + 'lapses, this page will say so and reconnecting takes a few seconds.',
        postingTitle: 'Where income is posted',
        postingBlurb: 'Taken from your own MYOB file. All three are required before MYOB can '
            + 'be enabled.',
        chooseAll: 'Choose an income account, a tax code and a deposit account.',
        notReady: 'Choose an income account, tax code and deposit account first',
        revokeNote: "This does not revoke Wadakaroo's access in your MYOB account — do that "
            + 'from MYOB if you need to.',
    },
};

// ── QuickBooks ──────────────────────────────────────────────────────
//
// QuickBooks bills invoice lines against an ITEM, not an account — the income
// account is a property of the item. So the first question is "which item",
// and QuickBooks decides where the revenue lands from there.

const QUICKBOOKS_TAB = {
    name: 'QuickBooks',
    softwareKey: 'QUICKBOOKS',
    queryKey: 'quickbooks',
    api: {
        authorizeUrl: getQuickBooksAuthorizeUrlApi,
        connection: getQuickBooksConnectionApi,
        referenceData: getQuickBooksReferenceDataApi,
        saveDefaults: saveQuickBooksDefaultsApi,
        disconnect: disconnectQuickBooksApi,
    },
    optionId: (o) => o.id,
    describeCompany: (c) => c.companyName || c.realmId,
    fields: [
        {
            key: 'itemRef',
            connectionKey: 'defaultItemRef',
            optionsKey: 'items',
            label: 'Invoice line item',
            hint: 'QuickBooks takes the income account from the item you choose.',
            format: (i) => (i.description ? `${i.name} — ${i.description}` : i.name),
            emptyHint: 'No active service items found in this QuickBooks company.',
            payload: (i) => ({ itemRef: i.id, itemName: i.name }),
        },
        {
            key: 'taxCodeRef',
            connectionKey: 'defaultTaxCodeRef',
            optionsKey: 'taxCodes',
            label: 'Tax code',
            format: (t) => (t.description ? `${t.name} — ${t.description}` : t.name),
            emptyHint: 'No tax codes found in this QuickBooks company.',
            payload: (t) => ({ taxCodeRef: t.id, taxCodeName: t.name }),
        },
        {
            key: 'depositAccountRef',
            connectionKey: 'defaultDepositAccountRef',
            optionsKey: 'depositAccounts',
            label: 'Deposit payments into',
            format: (a) => a.name,
            emptyHint: 'No bank accounts found in this QuickBooks company.',
            payload: (a) => ({ depositAccountRef: a.id, depositAccountName: a.name }),
        },
    ],
    copy: {
        companyLabel: 'Company',
        connectBlurb: "You'll be taken to Intuit to sign in and choose which QuickBooks company "
            + 'Wadakaroo may use. Nothing is sent until you reconcile a paid invoice.',
        consentNote: 'QuickBooks access is renewed automatically while you keep using '
            + 'Wadakaroo. If it ever lapses, this page will say so.',
        postingTitle: 'How invoices are recorded',
        postingBlurb: 'Taken from your own QuickBooks company. All three are required before '
            + 'QuickBooks can be enabled.',
        chooseAll: 'Choose an item, a tax code and a deposit account.',
        notReady: 'Choose an item, tax code and deposit account first',
        revokeNote: "Wadakaroo's access is revoked at Intuit's end as part of disconnecting.",
    },
};

// ============================================
// Xero Tab Content
// ============================================

const XeroTabContent = ({ integrations, togglingKey, onToggle }) => {
    const integration = integrations.find(i => i.software === 'XERO');
    const enabled = integration?.enabled || false;

    const activeOther = getActiveIntegration(integrations);
    const disabledByOther = activeOther && activeOther.software !== 'XERO';
    const activeLabel = disabledByOther
        ? ACCOUNTING_TABS.find(t => t.softwareKey === activeOther.software)?.label || activeOther.software
        : null;

    // ── State ────────────────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [existingConfig, setExistingConfig] = useState(null);
    const [isConfigured, setIsConfigured] = useState(false);

    const [accountCodeOptions, setAccountCodeOptions] = useState([]);
    const [taxTypeOptions, setTaxTypeOptions] = useState([]);

    const [formData, setFormData] = useState({
        clientId: '',
        clientSecret: '',
        selectedScopes: [],
        defaultAccountCode: '200',
        defaultTaxType: 'OUTPUT',
    });
    const [showClientSecret, setShowClientSecret] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const [isScopeModalOpen, setIsScopeModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // ── Fetch existing config + reference data on mount ──────────────

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [refData, config] = await Promise.all([
                    getXeroReferenceDataApi().catch(() => null),
                    getXeroConfigApi().catch(() => null),
                ]);

                if (cancelled) return;

                if (refData) {
                    setAccountCodeOptions(refData.accountCodes || []);
                    setTaxTypeOptions(refData.taxTypes || []);
                }

                if (config?.configured) {
                    setExistingConfig(config);
                    setIsConfigured(true);
                    setFormData({
                        clientId: config.clientId || '',
                        clientSecret: '',
                        selectedScopes: config.allowedApiScopes || [],
                        defaultAccountCode: config.defaultAccountCode || '200',
                        defaultTaxType: config.defaultTaxType || 'OUTPUT',
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    console.log('Error loading Xero config page data', err);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ── Handlers ─────────────────────────────────────────────────────

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
        setSuccessMessage(null);
    };

    const handleScopesChange = (newScopes) => {
        setFormData(prev => ({ ...prev, selectedScopes: newScopes }));
        if (errors.selectedScopes) setErrors(prev => ({ ...prev, selectedScopes: '' }));
        setSuccessMessage(null);
    };

    const removeScope = (scopeValue) => {
        setFormData(prev => ({
            ...prev,
            selectedScopes: prev.selectedScopes.filter(s => s !== scopeValue),
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.clientId.trim()) {
            newErrors.clientId = 'Client ID is required';
        }
        if (!formData.clientSecret.trim()) {
            newErrors.clientSecret = 'Client Secret is required';
        }
        if (formData.selectedScopes.length === 0) {
            newErrors.selectedScopes = 'Please select at least one API scope';
        }
        if (!formData.defaultAccountCode.trim()) {
            newErrors.defaultAccountCode = 'Account code is required';
        }
        if (!formData.defaultTaxType) {
            newErrors.defaultTaxType = 'Tax type is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setSaving(true);
        setErrors({});
        setSuccessMessage(null);

        try {
            const result = await saveXeroConfigApi({
                clientId: formData.clientId.trim(),
                clientSecret: formData.clientSecret.trim(),
                allowedApiScopes: formData.selectedScopes,
                defaultAccountCode: formData.defaultAccountCode.trim(),
                defaultTaxType: formData.defaultTaxType,
            });
            setExistingConfig(result);
            setIsConfigured(true);
            setSuccessMessage(isConfigured ? 'Configuration updated successfully' : 'Configuration saved successfully');
            setFormData(prev => ({ ...prev, clientSecret: '' }));
        } catch (err) {
            setErrors({ submit: err.response?.data?.message || 'Failed to save configuration. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteXeroConfigApi();
            setExistingConfig(null);
            setIsConfigured(false);
            setFormData({ clientId: '', clientSecret: '', selectedScopes: [], defaultAccountCode: '200', defaultTaxType: 'OUTPUT' });
            setSuccessMessage('Xero configuration deleted successfully');
            setIsDeleteModalOpen(false);
        } catch (err) {
            setErrors({ submit: err.response?.data?.message || 'Failed to delete configuration.' });
            setIsDeleteModalOpen(false);
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-AU', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const selectedTaxTypeDescription = taxTypeOptions.find(t => t.value === formData.defaultTaxType)?.description || '';
    const selectedAccountCodeOption = accountCodeOptions.find(a => a.value === formData.defaultAccountCode);

    // ── Loading state ────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[40vh]">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-500 text-sm">Loading Xero configuration...</p>
                </div>
            </div>
        );
    }

    // ── Render ────────────────────────────────────────────────────────

    // No max-width or horizontal padding here: this renders inside the page shell, which
    // already sets both. Repeating them inset the content twice.
    return (
        <div className="py-8">
            {/* Integration toggle */}
            <div className="mb-6">
                <IntegrationToggle
                    label="Xero"
                    enabled={enabled}
                    toggling={togglingKey === 'XERO'}
                    onToggle={() => onToggle('XERO', !enabled)}
                    disabled={disabledByOther}
                    disabledReason={`Disable ${activeLabel} first to enable Xero`}
                />
            </div>

            {/* Success message */}
            {successMessage && (
                <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-emerald-800 font-medium">{successMessage}</p>
                </div>
            )}

            {/* No config warning */}
            {!isConfigured && (
                <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-amber-900 mb-1">No Xero Configuration Found</h3>
                            <p className="text-sm text-amber-800">
                                You haven't configured your Xero integration yet. Enter your Custom Connection credentials below to get started.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Existing config info */}
            {isConfigured && existingConfig && (
                <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-semibold text-emerald-900 mb-1">Xero Integration Configured</h3>
                            <p className="text-sm text-emerald-800 mb-3">
                                Your Xero Custom Connection is set up and ready to use.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-emerald-700">
                                <div><span className="font-medium">Client ID:</span> {existingConfig.clientId}</div>
                                <div><span className="font-medium">Secret:</span> {existingConfig.clientSecretMasked}</div>
                                <div><span className="font-medium">Created:</span> {formatDate(existingConfig.createdAt)}</div>
                                <div><span className="font-medium">Last Updated:</span> {formatDate(existingConfig.updatedAt)}</div>
                                <div><span className="font-medium">Scopes:</span> {existingConfig.allowedApiScopes?.length || 0} configured</div>
                                <div><span className="font-medium">Account Code:</span> {existingConfig.defaultAccountCode || '200'}</div>
                                <div><span className="font-medium">Tax Type:</span> {existingConfig.defaultTaxType || 'OUTPUT'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Info banner */}
            <div className="mb-8 bg-gray-50 border border-gray-200 rounded-xl p-5">
                <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Info className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Setting up Xero Custom Connections</h3>
                        <p className="text-sm text-gray-600 mb-3">
                            To connect with Xero, you'll need to create a Custom Connection app in your Xero Developer Portal.
                            This provides secure machine-to-machine authentication using the client_credentials flow.
                        </p>
                        <a
                            href="https://developer.xero.com/documentation/guides/oauth2/custom-connections/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                        >
                            Learn more about Xero Custom Connections
                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>

            {/* Form card */}
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                {/* Credentials section */}
                <div className="p-6 sm:p-8 border-b border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">API Credentials</h2>
                            <p className="text-sm text-gray-500">
                                {isConfigured ? 'Update your Xero Custom Connection credentials' : 'Enter your Xero Custom Connection credentials'}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-5">
                        {/* Client ID */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Client ID <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="clientId"
                                value={formData.clientId}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all ${
                                    errors.clientId ? 'border-red-400 bg-red-50/50' : 'border-gray-200'
                                }`}
                                placeholder="Enter your Xero Client ID"
                            />
                            {errors.clientId && (
                                <p className="mt-2 text-sm text-red-600 flex items-center">
                                    <AlertCircle size={14} className="mr-1" /> {errors.clientId}
                                </p>
                            )}
                        </div>

                        {/* Client Secret */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Client Secret <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type={showClientSecret ? 'text' : 'password'}
                                    name="clientSecret"
                                    value={formData.clientSecret}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-3 pr-12 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all ${
                                        errors.clientSecret ? 'border-red-400 bg-red-50/50' : 'border-gray-200'
                                    }`}
                                    placeholder={isConfigured ? 'Enter new secret to update' : 'Enter your Xero Client Secret'}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowClientSecret(!showClientSecret)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    {showClientSecret ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {errors.clientSecret && (
                                <p className="mt-2 text-sm text-red-600 flex items-center">
                                    <AlertCircle size={14} className="mr-1" /> {errors.clientSecret}
                                </p>
                            )}
                            <p className="mt-2 text-xs text-gray-500">
                                Your credentials are encrypted and stored securely.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Invoice Defaults section */}
                <div className="p-6 sm:p-8 border-b border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Invoice Defaults</h2>
                            <p className="text-sm text-gray-500">Default account code and tax type applied to Xero invoice line items</p>
                        </div>
                    </div>

                    <div className="space-y-5">
                        {/* Default Account Code */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Default Account Code <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    name="defaultAccountCode"
                                    value={formData.defaultAccountCode}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all appearance-none bg-white ${
                                        errors.defaultAccountCode ? 'border-red-400 bg-red-50/50' : 'border-gray-200'
                                    }`}
                                >
                                    {accountCodeOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.value} — {opt.label}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                            {errors.defaultAccountCode && (
                                <p className="mt-2 text-sm text-red-600 flex items-center">
                                    <AlertCircle size={14} className="mr-1" /> {errors.defaultAccountCode}
                                </p>
                            )}
                            <p className="mt-2 text-xs text-gray-500">
                                {selectedAccountCodeOption?.description || 'The Xero account code used for invoice line items. Check your Xero Chart of Accounts for the correct code.'}
                            </p>
                        </div>

                        {/* Default Tax Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Default Tax Type <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <select
                                    name="defaultTaxType"
                                    value={formData.defaultTaxType}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-3 border rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all appearance-none bg-white ${
                                        errors.defaultTaxType ? 'border-red-400 bg-red-50/50' : 'border-gray-200'
                                    }`}
                                >
                                    {taxTypeOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label} ({opt.value})
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                            {errors.defaultTaxType && (
                                <p className="mt-2 text-sm text-red-600 flex items-center">
                                    <AlertCircle size={14} className="mr-1" /> {errors.defaultTaxType}
                                </p>
                            )}
                            <p className="mt-2 text-xs text-gray-500">
                                {selectedTaxTypeDescription || 'Select the tax treatment for invoice line items.'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* API Scopes section */}
                <div className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">API Scopes</h2>
                            <p className="text-sm text-gray-500">Select the permissions your integration requires</p>
                        </div>
                    </div>

                    {/* Scope selector button */}
                    <button
                        type="button"
                        onClick={() => setIsScopeModalOpen(true)}
                        className={`w-full px-4 py-3 border rounded-xl text-left flex items-center justify-between transition-all ${
                            errors.selectedScopes
                                ? 'border-red-400 bg-red-50/50'
                                : 'border-gray-200 hover:border-emerald-500 hover:bg-emerald-50/30'
                        }`}
                    >
                        <span className={formData.selectedScopes.length === 0 ? 'text-gray-400 text-sm' : 'text-gray-900 text-sm'}>
                            {formData.selectedScopes.length === 0
                                ? 'Click to select API scopes...'
                                : `${formData.selectedScopes.length} scope${formData.selectedScopes.length !== 1 ? 's' : ''} selected`}
                        </span>
                        <ChevronDown size={20} className="text-gray-400" />
                    </button>

                    {errors.selectedScopes && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                            <AlertCircle size={14} className="mr-1" /> {errors.selectedScopes}
                        </p>
                    )}

                    {/* Selected scopes chips */}
                    {formData.selectedScopes.length > 0 && (
                        <div className="mt-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Selected Scopes:</p>
                            <div className="flex flex-wrap gap-2">
                                {formData.selectedScopes.map(scopeValue => {
                                    const scope = XERO_API_SCOPES.find(s => s.value === scopeValue);
                                    return (
                                        <span
                                            key={scopeValue}
                                            className="inline-flex items-center px-3 py-1.5 rounded-full text-xs bg-emerald-100 text-emerald-700 font-medium"
                                        >
                                            {scope?.label || scopeValue}
                                            <button
                                                type="button"
                                                onClick={() => removeScope(scopeValue)}
                                                className="ml-2 hover:text-emerald-900 transition-colors"
                                            >
                                                <X size={14} />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Submit error */}
                    {errors.submit && (
                        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                            <p className="text-sm text-red-700 flex items-center">
                                <AlertCircle size={16} className="mr-2" /> {errors.submit}
                            </p>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="px-6 sm:px-8 py-6 bg-gray-50/50 border-t border-gray-100 flex flex-col sm:flex-row gap-3 sm:justify-between">
                    <div>
                        {isConfigured && (
                            <button
                                type="button"
                                onClick={() => setIsDeleteModalOpen(true)}
                                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors"
                            >
                                <Trash2 size={16} />
                                Delete Configuration
                            </button>
                        )}
                    </div>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium text-sm shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {saving ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                {isConfigured ? 'Updating...' : 'Saving...'}
                            </>
                        ) : (
                            <>
                                <CheckCircle size={18} />
                                {isConfigured ? 'Update Configuration' : 'Save Configuration'}
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Security note */}
            <div className="mt-6 flex items-start gap-3 px-4 py-3 bg-emerald-50/50 rounded-xl border border-emerald-100">
                <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-800">
                    <strong>Your data is secure.</strong> Credentials are encrypted and stored securely. We follow industry best practices for credential management.
                </p>
            </div>

            {/* Modals */}
            <ScopeSelectorModal
                isOpen={isScopeModalOpen}
                onClose={() => setIsScopeModalOpen(false)}
                selectedScopes={formData.selectedScopes}
                onScopesChange={handleScopesChange}
            />
            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                deleting={deleting}
            />
        </div>
    );
};

// ============================================
// AccountingSoftwarePage (tabbed container)
// ============================================

const AccountingSoftwarePage = () => {
    const [activeTab, setActiveTab] = useState('xero');
    const [integrations, setIntegrations] = useState([]);
    const [integrationsLoading, setIntegrationsLoading] = useState(true);
    const [togglingKey, setTogglingKey] = useState(null);
    const [toggleError, setToggleError] = useState(null);

    // ── Fetch integration states on mount ────────────────────────────

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await listAccountingIntegrationsApi();
                if (!cancelled) setIntegrations(data || []);
            } catch (err) {
                console.log('Error loading accounting integrations', err);
            } finally {
                if (!cancelled) setIntegrationsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ── Clear toggle error after 5 seconds ───────────────────────────

    useEffect(() => {
        if (!toggleError) return;
        const timer = setTimeout(() => setToggleError(null), 5000);
        return () => clearTimeout(timer);
    }, [toggleError]);

    // ── Toggle handler ───────────────────────────────────────────────

    const handleToggle = async (softwareKey, enabled) => {
        setTogglingKey(softwareKey);
        setToggleError(null);
        try {
            const updated = await toggleAccountingIntegrationApi({
                software: softwareKey,
                enabled,
            });
            setIntegrations(prev =>
                prev.map(i => i.software === softwareKey ? updated : i)
            );
        } catch (err) {
            const status = err.response?.status;
            const message = err.response?.data?.message;
            if (status === 409 && message) {
                setToggleError(message);
            } else {
                setToggleError('Failed to toggle integration. Please try again.');
            }
            console.error('Failed to toggle integration', err);
        } finally {
            setTogglingKey(null);
        }
    };

    // ── Derived: which integration is active ─────────────────────────

    const activeIntegration = getActiveIntegration(integrations);

    // ── Render ────────────────────────────────────────────────────────

    return (
        <Layout>
            {/* Left-aligned like every other page (Team Members, Invoices, Customers).
                mx-auto centred this one and made it the odd screen out.

                max-w-4xl stays: this is a form, and letting inputs stretch the full width
                of a wide monitor is harder to read, not easier. Capped AND left-aligned. */}
            <div className="max-w-4xl px-6 pt-8">
                {/* Page header */}
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-emerald-700" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Accounting Software</h1>
                            <p className="text-sm text-gray-500">
                                Connect and configure your accounting software integration. Only one can be active at a time.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Conflict error banner */}
                {toggleError && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-800 font-medium">{toggleError}</p>
                    </div>
                )}

                {/* Tabs */}
                <div className="border-b border-gray-200">
                    <nav className="flex gap-1" aria-label="Accounting software tabs">
                        {ACCOUNTING_TABS.map(tab => {
                            const isActive = activeTab === tab.id;
                            const integration = integrations.find(i => i.software === tab.softwareKey);
                            const isEnabled = integration?.enabled || false;
                            const isLockedOut = activeIntegration && activeIntegration.software !== tab.softwareKey;

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`relative px-5 py-3 text-sm font-medium transition-colors rounded-t-lg ${
                                        isActive
                                            ? 'text-emerald-700 bg-white border border-gray-200 border-b-white -mb-px'
                                            : isLockedOut
                                                ? 'text-gray-400 hover:text-gray-500 hover:bg-gray-50'
                                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="flex items-center gap-2">
                                        {tab.label}
                                        {!integrationsLoading && isEnabled && (
                                            <span className="w-2 h-2 rounded-full bg-emerald-500" title="Enabled" />
                                        )}
                                        {!integrationsLoading && isLockedOut && (
                                            <Lock size={12} className="text-gray-300" title={`Disabled — ${ACCOUNTING_TABS.find(t => t.softwareKey === activeIntegration.software)?.label} is active`} />
                                        )}
                                    </span>
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Tab content */}
            {integrationsLoading ? (
                <div className="flex items-center justify-center min-h-[40vh]">
                    <div className="text-center">
                        <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-4" />
                        <p className="text-gray-500 text-sm">Loading integrations...</p>
                    </div>
                </div>
            ) : (
                <>
                    {activeTab === 'xero' && (
                        <XeroTabContent
                            integrations={integrations}
                            togglingKey={togglingKey}
                            onToggle={handleToggle}
                        />
                    )}
                    {/* Both consent-based integrations render the same component; they differ
                        only in wording and in which options they ask for. Remounted per tab
                        via the key so switching tabs never shows one provider's state under
                        the other's heading. */}
                    {activeTab === 'myob' && (
                        <ConsentIntegrationTab
                            key="myob"
                            {...MYOB_TAB}
                            integrations={integrations}
                            togglingKey={togglingKey}
                            onToggle={handleToggle}
                        />
                    )}
                    {activeTab === 'quickbooks' && (
                        <ConsentIntegrationTab
                            key="quickbooks"
                            {...QUICKBOOKS_TAB}
                            integrations={integrations}
                            togglingKey={togglingKey}
                            onToggle={handleToggle}
                        />
                    )}
                </>
            )}
        </Layout>
    );
};

export default AccountingSoftwarePage;